import { BehaviorSubject, from, Observable, of, timer } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { Dependencies } from '../../../../configurations/dependencies/dependencies.interface';
import { Action } from '../../../state/action.type';
import { AppState } from '../../../state/app.state';
import {
  refreshTokenFailure,
  refreshTokenSuccess,
  signOut,
} from '../../../state/authentication/authentication.action';
import { SIGN_OUT } from '../../../state/authentication/authentication.action.type';
import { AUTH_USECASE_ERROR_MESSAGES } from '../../errors/usecases.errorMessages/authenticationUsecase.error.messages';
import { getTimeUntilRefresh } from '../../utils/token.utils';

export const refreshTokenUsecase = (
  action$: Observable<Action>,
  state$: BehaviorSubject<AppState>,
  dependencies: Dependencies,
): Observable<Action> => {
  // Watch for token changes in state
  return state$.pipe(
    map((state) => state.authentication.token),
    filter((token): token is string => !!token && typeof token === 'string'),
    switchMap((token) => {
      const timeUntilRefresh = getTimeUntilRefresh(token);
      if (typeof timeUntilRefresh !== 'number' || isNaN(timeUntilRefresh)) {
        return of(refreshTokenFailure('Invalid refresh time'), signOut());
      }
      console.log(
        'Token will be refreshed in:',
        Math.floor(timeUntilRefresh / 60000),
        'minutes',
      );

      return timer(timeUntilRefresh).pipe(
        mergeMap(() =>
          from(
            dependencies.backendAuthenticatorGateway.refreshToken(token),
          ).pipe(
            map((newToken) => refreshTokenSuccess(newToken)),

            catchError((error) => {
              console.warn('Token refresh failed:', error.message);
              return of(
                refreshTokenFailure(
                  AUTH_USECASE_ERROR_MESSAGES.SESSION_EXPIRED,
                ),
                signOut(),
              );
            }),
          ),
        ),
        // Stop this timer when a new token arrives or on logout
        takeUntil(action$.pipe(filter((action) => action.type === SIGN_OUT))),
      );
    }),
  );
};
